import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2 } from "lucide-react";
import { CuadraRuntimeProvider } from "./CuadraRuntimeProvider";
import { SimpleThread, type SimpleThreadHandle } from "./SimpleThread";
import { ArtifactLoaderGate } from "./ArtifactLoaderGate";
import { ArtifactPanel } from "./artifact/ArtifactPanel";
import { TexturedCard } from "../widget/components/TexturedCard";
import { CuadraWidgetProvider } from "../widget/context/CuadraWidgetContext";
import { CuadraChatClient } from "../lib/cuadraChatClient";
import type { ToolDefinition } from "../types/cuadra";
import { type CuadraChatStrings, resolveStrings } from "../types/strings";
import { useInjectStyles } from "../utils/useInjectStyles";
import { useModels } from "../hooks/useModels";
import { useModelSelection } from "../hooks/useModelSelection";

/**
 * Handle for controlling WidgetContent externally
 */
export interface WidgetContentHandle {
  /** Send a user message programmatically (triggers normal API call) */
  sendMessage: (message: string) => void;
  /** Send a pre-made Q&A pair (instant, simulated streaming, no API call) */
  sendPreMadeQA: (
    question: string,
    answer: string,
    options?: { delay?: number; streamingSpeed?: number },
  ) => void;
  /** Clear the current chat and start fresh */
  clearChat: () => void;
  /** Move keyboard focus to the composer input */
  focusInput: () => void;
}

// Export the config type for use in React components
export interface CuadraUIKitConfig {
  // API Configuration (choose one)
  /** Cuadra API base URL (e.g., 'https://api.cuadra.ai') */
  baseUrl?: string;
  /** Proxy URL for backend-handled authentication (e.g., '/api/chat') */
  proxyUrl?: string;

  // Authentication (optional if using proxyUrl)
  /** Bearer token for authentication (optional if backend handles auth) */
  sessionToken?: string | null;

  // Chat configuration
  /** Chat mode: single thread or multi-thread */
  mode?: "singleChat" | "multiChat";
  /** Model selection mode */
  modelMode?: "selector" | "fixed";
  /** Model ID (required if modelMode="fixed") */
  modelId?: string;
  /** Callback when model changes (selector mode) */
  onModelChange?: (modelId: string) => void;
  /** Create temporary chats that auto-delete */
  ephemeral?: boolean;
  /** System prompt for the assistant */
  systemPrompt?: string;
  /** Enable reasoning/thinking output for supported models (e.g., o1, o3) */
  enableReasoning?: boolean;
  /** Optional tools passed through to the chat API */
  tools?: ToolDefinition[];
  /** Optional callback invoked when the model emits a non-artifact tool call */
  onToolCall?: (
    toolName: string,
    args: Record<string, unknown>,
    ctx?: { lastUserText: string; modelId?: string },
  ) => void;

  // Thread configuration
  /** Load existing thread (multiChat mode) */
  initialThreadId?: string;
  /** When artifacts are enabled, open this artifact id or identifier after load (e.g. from `?artifact=`) */
  initialOpenArtifactId?: string;

  // UI Configuration
  /** Language/locale for i18n (e.g., 'en', 'es', 'fr') */
  language?: string;
  /** Welcome screen title */
  welcomeTitle?: string;
  /** Welcome screen subtitle */
  welcomeSubtitle?: string;
  /** Extra top padding for thread viewport (e.g., '1rem', '2rem') */
  extraTopPadding?: string;
  /** Suggestions to show in welcome screen (with optional pre-made responses) */
  suggestions?: Array<{
    /** Suggestion prompt text */
    prompt: string;
    /** Pre-made response (optional) - if provided, response appears instantly with simulated streaming */
    response?: string;
    /** Delay before showing response in ms (overrides preMadeResponseDelay) */
    responseDelay?: number;
  }>;
  /** Placeholder text for the input field */
  inputPlaceholder?: string;
  /** Default delay before pre-made responses in ms (default: 1000) */
  preMadeResponseDelay?: number;
  /** Speed of simulated streaming in ms per word (default: 50) */
  streamingSpeed?: number;

  // Theme configuration
  /** Show theme toggle button */
  showThemeToggle?: boolean;
  /** Initial theme ('light' | 'dark' | 'system') */
  theme?: "light" | "dark" | "system";

  // Container
  /** Container element ID (defaults to 'cuadra-chat') */
  containerId?: string;
  /** CSS classes to add to container element */
  containerClass?: string;

  // Callbacks
  /** Called when an error occurs */
  onError?: (error: Error) => void;
  /** Called when chat is created */
  onChatCreated?: (chatId: string) => void;
  /** Called when a user message is sent */
  onUserMessage?: () => void;
  /** Called when thread ID updates */
  onThreadIdUpdate?: (oldId: string, newId: string) => void;
  /** Called when logout button is clicked */
  onLogout?: () => void;
  /** Called when a 401 Unauthorized response is received */
  onUnauthorized?: () => void;
  /** Called when a chat response stream finishes */
  onStreamEnd?: (chatId: string) => void;
  /** Called when artifact generation begins (create_artifact tool call detected) */
  onArtifactGenerating?: (chatId: string) => void;
  /** Called when artifact generation completes (stream or background polling) */
  onArtifactComplete?: (chatId: string, artifactId?: string) => void;
  /** Enable file attachments in the chat input */
  enableAttachments?: boolean;
  /** Whether the current model supports vision/image inputs. When false, image attachments are rejected with an error. */
  supportsVision?: boolean;
  /** Initial message to send automatically when chat loads */
  initialMessage?: string;
  /** Initial pre-made Q&A to display when chat loads */
  initialPreMadeQA?: {
    question: string;
    answer: string;
  };
  /**
   * Interceptor called before each outgoing API request.
   * Return `true` to allow the request, `false` to block it.
   * Can be async (e.g., to show a login modal).
   */
  onBeforeRequest?: () => boolean | Promise<boolean>;
  /**
   * Mock response to show when onBeforeRequest returns false.
   * If set, instead of throwing an error, this message will be streamed as a response.
   * Can be a string or a function that returns a string (called each time a request is blocked).
   * Useful for showing helpful messages like "Complete setup before chatting".
   */
  mockResponseOnBlocked?: string | (() => string);
  /**
   * Hide the decorative border around the chat container
   * Useful for mobile apps or when embedding in a full-screen context
   * @default false
   */
  borderless?: boolean;
  /**
   * Apply safe area insets for mobile devices (notch, home indicator)
   * When true, adds padding to header, sidebar, and input areas
   * @default false
   */
  safeArea?: boolean;
  /**
   * Organization name to display in the sidebar header
   * Used for workspace branding in mobile apps
   */
  orgName?: string;
  /**
   * Primary color for branding (hex format)
   * Overrides the default primary color CSS variable
   * @example '#3B82F6'
   */
  primaryColor?: string;
  /** URL for the "Upgrade now" link shown on insufficient-credits errors */
  upgradeUrl?: string;
  /** Override UI strings for i18n / localization */
  strings?: Partial<CuadraChatStrings>;
  /** Credit/overage state from the billing system for proactive banners */
  creditState?: import("../types/props").CuadraCreditState;
  /** Enable AI artifact panel (documents, code, HTML, SVG, diagrams) */
  enableArtifacts?: boolean;
  /** Comma-separated model tiers the current plan can access (e.g. 'standard' or 'standard,premium') */
  modelTierAccess?: string;
  /** Stable id for this widget's runtime; isolates global error stores across mounts */
  errorSurfaceId?: string;
}

/**
 * Main widget content component - shared between library and widget builds
 * This component does NOT use createRoot - it's a regular React component
 */
export const WidgetContent = forwardRef<
  WidgetContentHandle,
  { config: CuadraUIKitConfig }
>(function WidgetContent({ config }, ref) {
  // Inject styles when component mounts
  useInjectStyles();

  // Ref to SimpleThread for external control
  const simpleThreadRef = useRef<SimpleThreadHandle>(null);

  // Track if initial message has been sent
  const initialMessageSentRef = useRef(false);

  const {
    baseUrl,
    proxyUrl,
    sessionToken,
    mode = "multiChat",
    modelMode = "fixed",
    modelId,
    onModelChange,
    ephemeral = false,
    systemPrompt,
    enableReasoning,
    initialThreadId,
    initialOpenArtifactId,
    welcomeTitle,
    welcomeSubtitle,
    extraTopPadding,
    suggestions,
    inputPlaceholder,
    preMadeResponseDelay = 1000,
    streamingSpeed = 50,
    // showThemeToggle kept in interface for consumer API but not used internally
    initialMessage,
    initialPreMadeQA,
    theme: initialTheme = "system",
    onError,
    onChatCreated,
    onUserMessage,
    onThreadIdUpdate,
    enableAttachments = false,
    supportsVision,
    onBeforeRequest,
    mockResponseOnBlocked,
    upgradeUrl,
    strings: stringsOverrides,
    creditState,
    onUnauthorized,
    onStreamEnd,
    errorSurfaceId,
  } = config;

  const strings = useMemo(
    () => resolveStrings(stringsOverrides),
    [stringsOverrides],
  );

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      sendMessage: (message: string) => {
        simpleThreadRef.current?.sendMessage(message);
      },
      sendPreMadeQA: (
        question: string,
        answer: string,
        options?: { delay?: number; streamingSpeed?: number },
      ) => {
        simpleThreadRef.current?.sendPreMadeQA(question, answer, options);
      },
      clearChat: () => {
        simpleThreadRef.current?.clearChat();
      },
      focusInput: () => {
        simpleThreadRef.current?.focusInput();
      },
    }),
    [],
  );

  // Handle initial message or pre-made Q&A on mount
  useEffect(() => {
    if (initialMessageSentRef.current) return;

    // Wait for chat to be ready before sending initial message
    const timer = setTimeout(() => {
      if (initialPreMadeQA) {
        initialMessageSentRef.current = true;
        simpleThreadRef.current?.sendPreMadeQA(
          initialPreMadeQA.question,
          initialPreMadeQA.answer,
          { delay: preMadeResponseDelay, streamingSpeed },
        );
      } else if (initialMessage) {
        initialMessageSentRef.current = true;
        simpleThreadRef.current?.sendMessage(initialMessage);
      }
    }, 500); // Wait 500ms for runtime to be ready

    return () => clearTimeout(timer);
  }, [initialMessage, initialPreMadeQA, preMadeResponseDelay, streamingSpeed]);

  // Determine the actual baseUrl and proxy mode
  const actualBaseUrl = proxyUrl || baseUrl || "";
  const isProxyMode = !!proxyUrl;

  // Create the models client once per baseUrl/proxyMode.  The session token
  // is updated via setSessionToken() so a refresh never recreates the client
  // (which would flip modelsLoading, trigger the loading gate, unmount
  // CuadraRuntimeProvider, and force a full chats reload).
  const client = useMemo(
    () =>
      new CuadraChatClient(
        actualBaseUrl,
        sessionToken || undefined,
        isProxyMode,
      ),
    [actualBaseUrl, isProxyMode],
  );

  useEffect(() => {
    client.setSessionToken(sessionToken || null);
  }, [client, sessionToken]);

  const { modelsData, modelsLoading, modelsError, models, fetchModels } =
    useModels(client, modelMode, config.modelTierAccess);

  const {
    currentModelId,
    handleModelChange,
    handleModelSelectorSync,
    resolvedSupportsVision,
    resolvedEnableReasoning,
    hasModel,
  } = useModelSelection({
    modelMode,
    modelId,
    modelsData,
    supportsVision,
    enableReasoning,
    onModelChange,
  });

  const [chatsLoading, setChatsLoading] = useState(false);

  useEffect(() => {
    if (mode === "multiChat") {
      setChatsLoading(true);
    }
  }, [mode]);

  const widgetContextValue = useMemo(
    () => ({
      currentModelId: currentModelId || "",
      models,
      modelsLoading,
      modelsError,
      onModelChange: handleModelChange,
      refetchModels: fetchModels,
    }),
    [
      currentModelId,
      models,
      modelsLoading,
      modelsError,
      handleModelChange,
      fetchModels,
    ],
  );

  const handleChatsLoaded = useCallback(() => {
    if (mode === "multiChat") {
      setChatsLoading(false);
    }
  }, [mode]);

  // Determine if we're still loading (models and/or chats)
  const isLoading =
    (modelMode === "selector" && modelsLoading) ||
    (mode === "multiChat" && chatsLoading);

  // Show loading state if we're still loading models or chats
  if (isLoading) {
    return (
      <CuadraWidgetProvider value={widgetContextValue}>
        <TexturedCard
          className="cuadra-h-full"
          paddingX="cuadra-px-0"
          paddingY="cuadra-py-0"
          borderSizePx={config.borderless ? 0 : 2}
        >
          <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full">
            <Loader2 className="cuadra-h-6 cuadra-w-6 cuadra-animate-spin cuadra-text-muted-foreground" />
          </div>
        </TexturedCard>
      </CuadraWidgetProvider>
    );
  }

  // Show error state if we need a model but don't have one
  if (modelMode === "selector" && !hasModel && modelsError) {
    return (
      <CuadraWidgetProvider value={widgetContextValue}>
        <TexturedCard
          className="cuadra-h-full"
          paddingX="cuadra-px-0"
          paddingY="cuadra-py-0"
        >
          <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full">
            <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-gap-2">
              <p className="cuadra-text-sm cuadra-text-destructive cuadra-font-brand">
                {strings.failedToLoadModels}
              </p>
            </div>
          </div>
        </TexturedCard>
      </CuadraWidgetProvider>
    );
  }

  return (
    <div
      className="cuadra-uikit cuadra-h-full cuadra-w-full"
      style={{ height: "100%", width: "100%" }}
    >
      <CuadraWidgetProvider value={widgetContextValue}>
        <CuadraRuntimeProvider
          baseUrl={isProxyMode ? actualBaseUrl : baseUrl || actualBaseUrl}
          sessionToken={sessionToken}
          isProxyMode={isProxyMode}
          mode={mode}
          modelMode={modelMode}
          modelId={currentModelId || modelId || undefined}
          onModelChange={handleModelChange}
          onModelSelectorSync={handleModelSelectorSync}
          ephemeral={ephemeral}
          systemPrompt={systemPrompt}
          enableReasoning={resolvedEnableReasoning}
          enableArtifacts={config.enableArtifacts}
          tools={config.tools}
          onToolCall={config.onToolCall}
          initialThreadId={initialThreadId}
          initialOpenArtifactId={initialOpenArtifactId}
          enableAttachments={enableAttachments}
          supportsVision={resolvedSupportsVision}
          onError={onError}
          onChatCreated={onChatCreated}
          onUserMessage={onUserMessage}
          onThreadIdUpdate={onThreadIdUpdate}
          onBeforeRequest={onBeforeRequest}
          mockResponseOnBlocked={mockResponseOnBlocked}
          onChatsLoaded={handleChatsLoaded}
          onUnauthorized={onUnauthorized}
          onStreamEnd={onStreamEnd}
          onArtifactGenerating={config.onArtifactGenerating}
          onArtifactComplete={config.onArtifactComplete}
          errorSurfaceId={errorSurfaceId}
        >
          <TexturedCard
            paddingX="cuadra-px-0"
            paddingY="cuadra-py-0"
            className={`cuadra-h-full ${config.containerClass || ""}`}
            borderSizePx={config.borderless ? 0 : 2}
            style={{ height: "100%" }}
          >
            <div
              className="cuadra-h-full cuadra-flex cuadra-relative cuadra-overflow-hidden"
              style={{ height: "100%" }}
            >
              {config.enableArtifacts && <ArtifactLoaderGate />}
              {/* min-w-[320px] prevents the artifact panel from squashing the chat below a readable width */}
              <div className="cuadra-flex-1 cuadra-flex cuadra-flex-col cuadra-min-w-[320px]">
                <div className="cuadra-flex-1 cuadra-relative cuadra-min-h-0">
                  <SimpleThread
                    ref={simpleThreadRef}
                    initialThreadId={initialThreadId}
                    welcomeTitle={welcomeTitle}
                    welcomeSubtitle={welcomeSubtitle}
                    extraTopPadding={extraTopPadding}
                    suggestions={suggestions}
                    inputPlaceholder={inputPlaceholder}
                    enableAttachments={enableAttachments}
                    preMadeResponseDelay={preMadeResponseDelay}
                    streamingSpeed={streamingSpeed}
                    safeArea={config.safeArea}
                    showChatHeader={mode === "multiChat"}
                    models={modelMode === "selector" ? models : undefined}
                    selectedModelId={currentModelId || undefined}
                    onModelChange={
                      modelMode === "selector" ? handleModelChange : undefined
                    }
                    theme={initialTheme}
                    upgradeUrl={upgradeUrl}
                    strings={strings}
                    creditState={creditState}
                  />
                </div>
              </div>
              {config.enableArtifacts && <ArtifactPanel />}
            </div>
          </TexturedCard>
        </CuadraRuntimeProvider>
      </CuadraWidgetProvider>
    </div>
  );
});
